import { apiDelete, apiGet, apiPatch, apiPost, apiPostForm } from 'api/service';

// Passive Intel Hub
export function getPassiveIntel(target, sources = 'crtsh,dns,whois,ip_rep') {
  return apiGet(`/api/passive-intel/?target=${encodeURIComponent(target)}&sources=${sources}`);
}

// Burp Suite XML import
export function importBurpXml(file, targetId = null) {
  const form = new FormData();
  form.append('file', file);
  if (targetId) form.append('target_id', targetId);
  return apiPostForm('/api/burp-import/', form);
}

// ─── Vulnerability Import (Nessus / OpenVAS / Nexpose / Qualys / CSV) ────────
export function getVulnImportFormats() {
  return apiGet('/api/vuln-import/formats/');
}

/**
 * Import one or more scan exports as a single assessment.
 *
 * `files` may be a single File or a list — a monthly assessment usually ships
 * as several exports, and they must land in one assessment so that comparing
 * months doesn't report the other file's hosts as "patched".
 *
 * `period` is the assessment month (e.g. '2026-07' or 'July 2026'). Omitted,
 * the backend uses the current month.
 */
export function importVulnerabilities(files, userTargetId, format = 'auto', period = '', scope = '') {
  const form = new FormData();
  const list = Array.isArray(files) ? files : [files];
  list.forEach((f) => form.append('file', f));
  form.append('user_target', userTargetId);
  form.append('format', format);
  if (period) form.append('period', period);
  form.append('scope', scope);
  return apiPostForm('/api/vuln-import/', form);
}

// Ask what the addresses in the files suggest ('internal', 'external' or '') without importing anything.
export function detectImportScope(files, format = 'auto') {
  const form = new FormData();
  files.forEach((f) => form.append('file', f));
  form.append('format', format);
  return apiPostForm('/api/vuln-import/detect/', form);
}

// Correct where an imported assessment was run from.
export function setAssessmentScope(scanId, scope) {
  return apiPost(`/api/vuln-assessments/${scanId}/scope/`, { scope });
}

// Imported assessments of a workspace, each compared with the previous import of the same scope.
export function getVulnAssessments(userTargetId) {
  return apiGet(`/api/vuln-assessments/?user_target=${encodeURIComponent(userTargetId)}`);
}

// ─── Surface Search (Shodan / FOFA / ZoomEye / hunter.how) ──────────────────
export function getSurfaceSearchEngines() {
  return apiGet('/api/surface-search/engines/');
}

export function surfaceSearch(query, engines = [], limit = 50) {
  const params = new URLSearchParams({ query, limit: String(limit) });
  if (engines && engines.length) params.set('engines', engines.join(','));
  return apiGet(`/api/surface-search/?${params.toString()}`);
}

// DNS blacklist check of an IP or name
export function checkBlacklist(target) {
  return apiPost('/api/blacklist/check/', { target });
}

// Blacklist watch: addresses checked every day
export const listBlacklistWatches = () => apiGet('/api/blacklist-watch/');
export const addBlacklistWatch = (host) => apiPost('/api/blacklist-watch/', { host });
export const updateBlacklistWatch = (id, body) => apiPatch(`/api/blacklist-watch/${id}/`, body);
export const removeBlacklistWatch = (id) => apiDelete(`/api/blacklist-watch/${id}/`);
export const checkBlacklistWatch = (id) => apiPost(`/api/blacklist-watch/${id}/check/`, {});
export const listBlacklistEvents = () => apiGet('/api/blacklist-watch/events/?limit=30');
